import { IHistory } from '@monetize/types/app';
import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import { formatInteger } from '@monetize/utils/core';
import { useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useGetTeamHistory } from '../../../../../api/settingsService';
import {
  MBox,
  MButton,
  MCircularProgress,
  MCustomIconButton,
  MFlex,
  MHeading,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import MEmptyDataPlaceholder from '../../../../../components/Monetize/MEmptyDataPlaceholder';
import { SETTINGS_PANE_HEIGHT } from '../../../SideNavBar/SettingsSideNav';
import { TeamHistoryCard } from '../components/TeamHistoryCard';

const HISTORY_PAGE_INCREMENT = 20;

interface TeamHistoryPanelProps {
  teamId: string;
  onClose: () => void;
}

/**
 * Full-height side column replacing the former history drawer. It sticks to
 * the top of the scrolling settings pane and scrolls its own list, so it never
 * grows past that pane while the members table scrolls beneath it.
 */
export const TeamHistoryPanel = ({
  teamId,
  onClose,
}: TeamHistoryPanelProps) => {
  const historyBodyRef = useRef<HTMLDivElement | null>(null);
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);

  const {
    isLoading: historyLoading,
    data: teamHistory,
    isFetching,
  } = useGetTeamHistory({ config: pager, teamId });

  const scrollToTop = () => {
    historyBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const shouldRenderLoadMoreBtn = (teamHistory?.totalPages ?? 0) > 1;
  const hasHistoryRecordContent = (teamHistory?.content.length ?? 0) > 0;

  return (
    <MFlex
      direction="column"
      w="360px"
      flexShrink={0}
      position="sticky"
      top={0}
      h={SETTINGS_PANE_HEIGHT}
      maxH={SETTINGS_PANE_HEIGHT}
      borderLeft="1px solid"
      borderColor="border.subtle"
      bgColor="bg.surface"
      data-testid="team-history-panel"
    >
      <MFlex
        justify="space-between"
        align="center"
        px={3}
        py={2}
        flexShrink={0}
        borderBottom="1px solid"
        borderColor="border.subtle"
      >
        <MHeading size="sm" as="h4" fontWeight="semibold">
          Team History
        </MHeading>
        <MCustomIconButton
          icon={MdClose}
          variant="icon"
          aria-label="Close team history"
          onClick={onClose}
          data-testid="team-history-close-btn"
        />
      </MFlex>
      <MBox
        ref={historyBodyRef}
        flex={1}
        minH={0}
        overflowY="auto"
        px={2}
        py={2}
        bgColor="tGray.sidebarDark"
      >
        {historyLoading && (
          <MFlex w="full" py={8} justify="center" align="center">
            <MCircularProgress isIndeterminate />
          </MFlex>
        )}
        {!historyLoading && !hasHistoryRecordContent && (
          <MFlex flexDirection="column" bgColor="tWhite.base">
            <MEmptyDataPlaceholder
              mainMessage="Looks like there is no team history here."
              boxProps={{ h: '3.25rem' }}
              mainMessageProps={{ fontSize: 'md', fontWeight: 'semibold' }}
            />
          </MFlex>
        )}

        <MStack gap={2}>
          {teamHistory?.content?.map((historyRecord: IHistory, index) => (
            <TeamHistoryCard
              key={`${historyRecord.createDate}-${index}`}
              message={historyRecord.message}
              time={historyRecord.createDate}
            />
          ))}
        </MStack>

        {hasHistoryRecordContent && (
          <MFlex align="center" justify="center" flexDir="column" mt={4}>
            <MText textAlign="center" fontSize="xs" color="tPurple.base">
              {`Showing ${formatInteger(teamHistory?.content.length)} of ${formatInteger(teamHistory?.totalElements)}`}
            </MText>
            {shouldRenderLoadMoreBtn && (
              <MButton
                isLoading={!historyLoading && isFetching}
                variant="tertiary"
                onClick={() =>
                  setPager((currentPager) => ({
                    ...currentPager,
                    rows: currentPager.rows + HISTORY_PAGE_INCREMENT,
                  }))
                }
              >
                Load More
              </MButton>
            )}
            <MButton variant="tertiary" onClick={scrollToTop} mt={2}>
              Back to Top
            </MButton>
          </MFlex>
        )}
      </MBox>
    </MFlex>
  );
};
